import { useCallback, useEffect, useMemo, useState } from "react";

import { RichTextEditor } from "@/components/rich-text-editor";
import { formatTimeAgo, useAutosave } from "@/lib/hooks/use-autosave";
import {
  useAdminExperience,
  useCreateExperience,
  useDeleteExperience,
  useReorderExperience,
  useUpdateExperience,
  type WorkExperience,
} from "@/lib/queries/use-experience";

interface ExperienceFormData {
  company: string;
  role: string;
  period: string;
  description: string;
  url: string;
}

const emptyForm: ExperienceFormData = {
  company: "",
  role: "",
  period: "",
  description: "",
  url: "",
};

export function AdminExperience() {
  const { data: experience, isLoading } = useAdminExperience();
  const createExperience = useCreateExperience();
  const updateExperience = useUpdateExperience();
  const deleteExperience = useDeleteExperience();
  const reorderExperience = useReorderExperience();

  const [editingId, setEditingId] = useState<number | "new" | null>(null);
  const [formData, setFormData] = useState<ExperienceFormData>(emptyForm);
  const [localExperience, setLocalExperience] = useState<WorkExperience[]>([]);

  // Sync local state with server data
  useEffect(() => {
    if (experience) {
      setLocalExperience(experience);
    }
  }, [experience]);

  // Autosave for form
  const handleAutosave = useCallback(async () => {
    if (editingId === null || editingId === "new") return;

    await updateExperience.mutateAsync({
      id: editingId,
      company: formData.company,
      role: formData.role,
      period: formData.period,
      description: formData.description || undefined,
      url: formData.url || undefined,
    });
  }, [editingId, formData, updateExperience]);

  const autosaveData = useMemo(() => formData, [formData]);

  const { lastSaved, isSaving, hasUnsavedChanges, saveNow, markAsSaved } = useAutosave({
    data: autosaveData,
    onSave: handleAutosave,
    enabled: editingId !== null && editingId !== "new",
  });

  const handleEdit = (item: WorkExperience) => {
    setEditingId(item.id);
    setFormData({
      company: item.company,
      role: item.role,
      period: item.period,
      description: item.description || "",
      url: item.url || "",
    });
    markAsSaved();
  };

  const handleNew = () => {
    setEditingId("new");
    setFormData(emptyForm);
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData(emptyForm);
  };

  const handleSave = async () => {
    if (editingId === "new") {
      const maxOrder = localExperience.reduce((max, e) => Math.max(max, e.display_order), -1);
      await createExperience.mutateAsync({
        company: formData.company,
        role: formData.role,
        period: formData.period,
        description: formData.description || undefined,
        url: formData.url || undefined,
        display_order: maxOrder + 1,
      });
    } else if (editingId !== null) {
      await updateExperience.mutateAsync({
        id: editingId,
        company: formData.company,
        role: formData.role,
        period: formData.period,
        description: formData.description || undefined,
        url: formData.url || undefined,
      });
    }
    setEditingId(null);
    setFormData(emptyForm);
  };

  const handleDelete = async (id: number) => {
    if (confirm("Are you sure you want to delete this experience?")) {
      await deleteExperience.mutateAsync(id);
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const newOrder = [...localExperience];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setLocalExperience(newOrder);
    await reorderExperience.mutateAsync(newOrder.map((e) => e.id));
  };

  const handleMoveDown = async (index: number) => {
    if (index === localExperience.length - 1) return;
    const newOrder = [...localExperience];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setLocalExperience(newOrder);
    await reorderExperience.mutateAsync(newOrder.map((e) => e.id));
  };

  // Status indicator
  const SaveStatus = () => {
    if (editingId === "new") return null;

    if (isSaving) {
      return (
        <div className="flex items-center gap-2">
          <div className="size-2 bg-yellow-500 animate-pulse" />
          <span className="text-[11px] text-text-muted">Saving...</span>
        </div>
      );
    }

    if (hasUnsavedChanges) {
      return (
        <div className="flex items-center gap-2">
          <div className="size-2 bg-orange-500" />
          <span className="text-[11px] text-text-muted">Unsaved changes</span>
        </div>
      );
    }

    if (lastSaved) {
      return (
        <div className="flex items-center gap-2">
          <div className="size-2 bg-green-500" />
          <span className="text-[11px] text-text-muted">Saved {formatTimeAgo(lastSaved)}</span>
        </div>
      );
    }

    return null;
  };

  if (isLoading) {
    return (
      <div>
        <p className="text-text-muted">Loading experience...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <h1 className="text-[24px] md:text-[28px] font-bold text-primary">Work Experience</h1>
        {editingId === null ? (
          <button
            onClick={handleNew}
            className="px-4 py-2 bg-primary text-background text-[13px] font-bold hover:opacity-90 active:opacity-75 transition-opacity"
          >
            + Add Experience
          </button>
        ) : (
          <div className="flex items-center gap-4">
            <SaveStatus />
            {editingId !== "new" && (
              <button
                onClick={saveNow}
                disabled={isSaving || !hasUnsavedChanges}
                className="px-4 py-2 bg-primary text-background text-[13px] font-bold hover:opacity-90 active:opacity-75 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Edit Form */}
      {editingId !== null && (
        <div className="border border-border bg-card p-4 md:p-6 mb-6">
          <h2 className="text-[11px] text-text-muted font-bold tracking-caps uppercase mb-4">
            {editingId === "new" ? "New Experience" : "Edit Experience"}
          </h2>

          <div className="space-y-4 max-w-2xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
                  Company
                </label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background border border-border text-text-primary text-[13px] focus:outline-none focus:border-border-hover hover:border-border-subtle transition-colors"
                  placeholder="e.g., SpaceX"
                />
              </div>
              <div>
                <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
                  Role
                </label>
                <input
                  type="text"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background border border-border text-text-primary text-[13px] focus:outline-none focus:border-border-hover hover:border-border-subtle transition-colors"
                  placeholder="e.g., Software Engineer"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
                  Period
                </label>
                <input
                  type="text"
                  value={formData.period}
                  onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background border border-border text-text-primary text-[13px] focus:outline-none focus:border-border-hover hover:border-border-subtle transition-colors"
                  placeholder="e.g., Jan 2023 - Present"
                />
              </div>
              <div>
                <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
                  URL (optional)
                </label>
                <input
                  type="text"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background border border-border text-text-primary text-[13px] focus:outline-none focus:border-border-hover hover:border-border-subtle transition-colors"
                  placeholder="e.g., https://company.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
                Description
              </label>
              <div className="border border-border">
                <RichTextEditor
                  content={formData.description}
                  onChange={(html) => setFormData({ ...formData, description: html })}
                  placeholder="Describe your role and accomplishments..."
                />
              </div>
              <p className="text-[11px] text-text-faint mt-1">
                Use bullet points, bold text, and links to highlight key achievements.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleSave}
                disabled={!formData.company || !formData.role || !formData.period}
                className="px-4 py-2 bg-primary text-background text-[13px] font-bold hover:opacity-90 active:opacity-75 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingId === "new" ? "Create" : "Save"}
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-border text-text-secondary text-[13px] hover:bg-card active:bg-border transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Experience List */}
      <div className="space-y-3">
        {localExperience.map((item, index) => (
          <div
            key={item.id}
            className="border border-border bg-card p-4 hover:border-border-hover transition-colors"
          >
            <div className="flex items-start gap-3">
              {/* Reorder buttons */}
              <div className="flex flex-col gap-0.5">
                <button
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                  className="text-text-faint hover:text-text-secondary hover:bg-border active:bg-border-hover disabled:opacity-30 disabled:cursor-not-allowed p-2 transition-colors"
                  title="Move up"
                >
                  <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="square"
                      strokeLinejoin="miter"
                      strokeWidth={2}
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => handleMoveDown(index)}
                  disabled={index === localExperience.length - 1}
                  className="text-text-faint hover:text-text-secondary hover:bg-border active:bg-border-hover disabled:opacity-30 disabled:cursor-not-allowed p-2 transition-colors"
                  title="Move down"
                >
                  <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="square"
                      strokeLinejoin="miter"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                  <h3 className="text-[15px] font-bold text-text-primary">{item.company}</h3>
                  <span className="text-[11px] text-text-faint">{item.period}</span>
                </div>
                <p className="text-[13px] text-text-tertiary mb-2">{item.role}</p>
                {item.description && (
                  <div
                    className="text-[12px] text-text-muted line-clamp-2 [&_p]:inline [&_ul]:hidden [&_ol]:hidden"
                    dangerouslySetInnerHTML={{ __html: item.description }}
                  />
                )}
              </div>

              {/* Actions - larger touch targets */}
              <div className="flex gap-1 shrink-0">
                <button
                  onClick={() => handleEdit(item)}
                  className="text-[12px] text-text-muted hover:text-primary active:text-primary/80 px-3 py-2 transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  className="text-[12px] text-text-muted hover:text-destructive active:text-destructive/80 px-3 py-2 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}

        {localExperience.length === 0 && (
          <div className="border border-dashed border-border-hover py-12 text-center">
            <p className="text-text-muted text-[14px]">No work experience added yet.</p>
            <button onClick={handleNew} className="mt-3 text-[13px] text-primary hover:underline">
              + Add your first entry
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
